import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AbstractAuditTrailService } from '@iot-platform/audit-trail';
import { CustomEncoder, ENVIRONMENT } from '@iot-platform/core';
import { ApiHelpers, DiffUtils, ExportHelpers } from '@iot-platform/iot-platform-utils';
import { HistoryLog, HistoryLogDetail } from '@iot-platform/models/audit-trail';
import { CommonApiListResponse, CommonApiRequest, CommonApiResponse, Environment, Pagination } from '@iot-platform/models/common';
import { get } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

type HistoryLogParams = CommonApiRequest;

@Injectable({
  providedIn: 'root'
})
export class AuditTrailService extends AbstractAuditTrailService<Partial<HistoryLogParams>> {
  protected readonly httpClient: HttpClient = inject(HttpClient);
  protected readonly environment: Environment = inject(ENVIRONMENT);

  getBaseUrl(id: string) {
    // TODO should be centralized in backend and expose a common audit trail endpoint
    return `${this.environment.api.url_connector}${this.environment.api.endpoints.deviceDetails}/${id}/trails`;
  }

  getAll(request: Partial<HistoryLogParams>): Observable<CommonApiResponse<HistoryLog, Pagination>> {
    let params: HttpParams = new HttpParams({ encoder: new CustomEncoder() });
    params = params.set('limit', 500);
    return this.httpClient
      .get<CommonApiListResponse<HistoryLog>>(this.getBaseUrl(request.additionalOptions.elementId), { params })
      .pipe(map((response: CommonApiListResponse<HistoryLog>) => ApiHelpers.getHttpResponse<HistoryLog>(response)))
      .pipe(
        map((response: CommonApiResponse<HistoryLog, Pagination>) => ({
          ...response,
          data: response.data.map((item) => ({ ...item, concept: request.additionalOptions.concept }))
        }))
      );
  }

  getById(params: { id: string } & Partial<HistoryLogParams>): Observable<HistoryLogDetail> {
    return this.httpClient.get<HistoryLogDetail>(`${this.getBaseUrl(params.additionalOptions.elementId)}/${params.id}`).pipe(
      map((logDetail: HistoryLogDetail) => {
        const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
        const predicate = (a: { v: number | string }, b: { v: number | string }): boolean => a !== b;

        logDetail.updatedAttributes.forEach((attr: string) => {
          const oldVal = get(logDetail, ['oldImage', attr]);
          const newVal = get(logDetail, ['newImage', attr]);
          if (isRecord(oldVal) || isRecord(newVal)) {
            logDetail.oldImage[attr] = { ...DiffUtils.diffObjectsWith(oldVal ?? {}, newVal ?? {}, predicate) };
            logDetail.newImage[attr] = { ...DiffUtils.diffObjectsWith(newVal ?? {}, oldVal ?? {}, predicate) };
          }
        });

        return logDetail;
      })
    );
  }

  exportLogDetails(logDetails: HistoryLogDetail): void {
    if (logDetails) {
      ExportHelpers.toJson(logDetails.conceptId, JSON.stringify(get(logDetails, ['newImage'], ''), null, 4));
    }
  }
}
